'use client';

import { Bunja, bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { addDays, isBefore } from 'date-fns';
import { Atom, atom, useAtomValue } from 'jotai';
import { AtomWithQueryResult, atomWithQuery } from 'jotai-tanstack-query';

import { ValidKycLevels } from '@flipster/apis/v1/kyc/types';
import { getMeApi, postUserLogOutApi } from '@flipster/apis/v1/user';
import type { User } from '@flipster/apis/v1/user/types';
import { alert } from '@flipster/ui/Alert';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { CookieKeys, getCookieBunja } from '@flipster/utils/cookie';
import { datadog } from '@flipster/utils/datadog';
import { convertDate } from '@flipster/utils/datetime';
import deferred from '@flipster/utils/deferred';
import { getErrorCode } from '@flipster/utils/error';
import { deleteLocalStorage, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { SessionStorageKey, setSessionStorage } from '@flipster/utils/sessionStorage';
import webviewHandler from '@flipster/utils/webview-handler';

export const nutsBunja = getCookieBunja(CookieKeys.Nuts);
const internalCookieBunja = getCookieBunja(CookieKeys.Internal);

export interface AuthBunjaInstance {
  getMeAtom: Atom<AtomWithQueryResult<User, unknown>>;
  userAtom: Atom<User | undefined>;
  isLoggedInAtom: Atom<boolean>;
  /**
   * API 요청시 로그인 여부를 빠르게 판별하기 위한 Atom
   *
   * 로그인이 풀려있더라도 서버가 쿠키를 보고 오류 응답을 돌려줄 것이기 때문에
   * API 호출시에는 쿠키가 존재하는지 정도만 확인해도 괜찮음
   *
   * UI를 그리는 코드에서는 `isLoggedInAtom`을 사용해야 함
   */
  isCookieLoggedInAtom: Atom<boolean>;
  isTfaEnabledAtom: Atom<boolean>;
  hasDepositedAtom: Atom<boolean>;
  isValidKycAtom: Atom<boolean>;
  isKolAtom: Atom<boolean>;
  isNewUserAtom: Atom<boolean>;
  logout(): void;
  checkSessionError(err: unknown): void;
  waitLoading(): Promise<void>;
}

export const getMeQueryKey = 'getMeApi';

export const authBunja: Bunja<AuthBunjaInstance> = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const nutsBunjaInstance = bunja.use(nutsBunja);
  const internalCookieBunjaInstance = bunja.use(internalCookieBunja);
  const { cookieAtom, sync, remove } = nutsBunjaInstance;
  const { update: setInternalCookie } = internalCookieBunjaInstance;

  async function logout() {
    try {
      const currentNuts = store.get(cookieAtom);
      if (currentNuts) await postUserLogOutApi(currentNuts);
    } catch (error) {
      const errorCode = getErrorCode(error);
      if (errorCode === 'api.invalidParameter') {
        alert.error(error);
      }
    } finally {
      deleteLocalStorage('flipster.configs.v1.query.cache');
      remove();
      setInternalCookie('false', 3);
      segment.reset();
      // 웹뷰에서는 새로고침시 쿠키가 안지워지는 이슈가 있음
      webviewHandler.postMessage({ action: 'link', params: { page: 'signin' } }, () => window.location.reload());
    }
  }

  function sessionErrorHandler(err: unknown) {
    if (checkAxiosError(err) && err.response?.status === 401) {
      const errorCode = getErrorCode(err);
      if (errorCode) setSessionStorage(SessionStorageKey.AuthErrorMessage, errorCode);
      logout();
    }
  }

  const getMeAtom = atomWithQuery((get) => {
    const nuts = get(cookieAtom);
    return {
      queryKey: [getMeQueryKey],
      enabled: !!nuts,
      async queryFn() {
        try {
          const res = await getMeApi();
          sync();
          const loginUser = res.data.user;
          const userId = loginUser.userId.toString();
          segment.identify(userId, loginUser.uid);
          datadog.setUser({ id: userId, email: loginUser.email, uid: loginUser.uid });
          setInternalCookie(loginUser.isInternal ? 'true' : 'false', 3);
          return loginUser;
        } catch (err) {
          sessionErrorHandler(err);
          throw err;
        }
      },
    };
  });
  getMeAtom.debugLabel = 'auth - getMe';

  const userAtom = atom((get) => get(getMeAtom).data);
  userAtom.debugLabel = 'auth - user';

  // NOTE: 인규님 요청으로 accountId 대신 userId로 판별
  const isLoggedInAtom = atom((get) => {
    const user = get(userAtom);
    return !!user?.userId && user?.emailVerified;
  });
  isLoggedInAtom.debugLabel = 'auth - is logged in';

  const isCookieLoggedInAtom = atom((get) => {
    const nuts = get(cookieAtom);
    const isLoggedIn = get(isLoggedInAtom);
    return !!nuts || isLoggedIn;
  });
  isCookieLoggedInAtom.debugLabel = 'auth - is cookie logged in';

  const isTfaEnabledAtom = atom((get) => {
    const user = get(userAtom);
    return !!user?.tfaEnabled;
  });
  isTfaEnabledAtom.debugLabel = 'auth - is tfa enabled';

  const hasDepositedAtom = atom((get) => {
    const user = get(userAtom);
    return !!user?.firstDepositAt;
  });
  hasDepositedAtom.debugLabel = 'auth - has deposited';

  const isValidKycAtom = atom((get) => {
    const user = get(userAtom);
    return !!user?.kycLevel && ValidKycLevels.includes(user.kycLevel);
  });
  isValidKycAtom.debugLabel = 'auth - is valid kyc';

  const isKolAtom = atom((get) => {
    const user = get(userAtom);
    return !!user?.isAffiliate;
  });
  isKolAtom.debugLabel = 'auth - is kol';

  const isNewUserAtom = atom((get) => {
    const user = get(userAtom);
    return !!user && isBefore(convertDate(user?.createdAt)!, addDays(new Date(), 14));
  });
  isNewUserAtom.debugLabel = 'auth - is new user';

  function waitLoading(): Promise<void> {
    const { isInitialLoading: isLoading } = store.get(getMeAtom);
    if (!isLoading) return Promise.resolve();
    const result = deferred<void>();
    const unsub = store.sub(getMeAtom, () => {
      const { isInitialLoading } = store.get(getMeAtom);
      if (!isInitialLoading) {
        result.resolve();
        unsub();
      }
    });
    return result;
  }

  function checkSessionError(err: unknown) {
    const isLoggedIn = store.get(isLoggedInAtom);
    if (isLoggedIn) sessionErrorHandler(err);
  }

  return {
    logout,
    checkSessionError,
    getMeAtom,
    userAtom,
    isLoggedInAtom,
    isCookieLoggedInAtom,
    isTfaEnabledAtom,
    hasDepositedAtom,
    isValidKycAtom,
    isKolAtom,
    isNewUserAtom,
    waitLoading,
  };
});
authBunja.debugLabel = 'authBunja';

// `getMe` 응답을 받기 전에 user id를 빠르게 얻고 싶은 경우 사용합니다.
export const userIdBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const authBunjaInstance = bunja.use(authBunja);
  const nutsBunjaInstance = bunja.use(nutsBunja);
  const { userAtom } = authBunjaInstance;
  const { cookieAtom } = nutsBunjaInstance;

  const userIdAtom = atom((get) => {
    const user = get(userAtom);
    const nuts = get(cookieAtom);
    if (user) return String(user.userId);
    if (!nuts) return;
    const nutsAndUserId = getLocalStorage('flipster.configs.v1.nutsAndUserId');
    if (!nutsAndUserId) return;
    const fragments = nutsAndUserId.split('&');
    if (nuts !== fragments[0]) return;
    return fragments[1];
  });

  // '0'으로 앞자리 패딩된 최소 5글자짜리 user id
  // amplitude, segment 등에서 이벤트 추적 용도로 사용
  const userIdForTrackAtom = atom((get) => {
    const userId = get(userIdAtom);
    if (!userId) return;
    return userId.padStart(5, '0');
  });

  const nutsAndUserIdAtom = atom((get) => {
    const nuts = get(cookieAtom);
    const user = get(userAtom);
    if (!nuts || !user) return;
    // nuts는 base64 문자열이라고 기대할 수 있음.
    // 따라서 구분자로 &를 사용해도 안전.
    return `${nuts}&${user.userId}`;
  });

  bunja.effect(() => {
    writeToLocalStorage();
    return store.sub(nutsAndUserIdAtom, writeToLocalStorage);
    function writeToLocalStorage() {
      const nutsAndUserId = store.get(nutsAndUserIdAtom);
      if (nutsAndUserId) {
        setLocalStorage('flipster.configs.v1.nutsAndUserId', nutsAndUserId);
      } else {
        deleteLocalStorage('flipster.configs.v1.nutsAndUserId');
      }
    }
  });

  return {
    userIdAtom,
    userIdForTrackAtom,
  };
});

export function useAuth() {
  const {
    logout,
    getMeAtom,
    isLoggedInAtom,
    isCookieLoggedInAtom,
    isTfaEnabledAtom,
    hasDepositedAtom,
    isValidKycAtom,
    isKolAtom,
    isNewUserAtom,
  } = useBunja(authBunja);

  const { isInitialLoading: isLoading, data: user, refetch: getMe } = useAtomValue(getMeAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);
  const isTfaEnabled = useAtomValue(isTfaEnabledAtom);
  const hasDeposited = useAtomValue(hasDepositedAtom);
  const isValidKyc = useAtomValue(isValidKycAtom);
  const isKol = useAtomValue(isKolAtom);
  const isNewUser = useAtomValue(isNewUserAtom);

  return {
    isLoading,
    isLoggedIn,
    isKol,
    isCookieLoggedIn,
    isTfaEnabled,
    hasDeposited,
    user,
    getMe,
    isValidKyc,
    isNewUser,
    logout,
  };
}
